import React, { useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams } from 'react-router-dom'
import cx from 'classnames'

import Html from '@components/page/Html'
import Masonry from '@components/page/Masonry'
import DirectoryMasonry from '@components/page/DirectoryMasonry'
import Slideshow from '@components/page/Slideshow'
import BlockRenderer, { OverlayBlocks } from '@components/page/BlockRenderer'
import FullscreenBackground from '@components/shared/FullscreenBackground'
import NotFound from '@app/(website)/not-found'
import { useAppContext } from '@/context/AppContext'
import { findPageByPath, findPageBySlug, getPagePath, getParentId } from '@/utils/pagePaths'
import { trackProjectViewed } from '@/services/analytics'
import { useEditorFrame, useBlockEditing } from '@components/preview/editGates'
import { resolvePageShareTitle, resolveShareTitle, shareTitleVarsForPage } from '@/share/titleTemplate'
import { compactMeta, shareImageMeta, shareSiteTitle } from '@/share/meta'
import { hasBackground } from '@/background/sets'

/**
 * Public page route (non-image).
 * Handles any page slug (including nested slugs).
 * Appearance live preview overlays drafts via ThemePreviewBridge / AppContext
 * (appearancePreview=1), not a separate pagePreview protocol.
 */
function PublicPageRoute({ match: matchProp }) {
    const params = useParams()
    const match = matchProp ?? { params }
    const { content, urlBase, theme } = useAppContext()

    const urlPath = match?.params?.slug || ''
    const pages = content?.pages || {}
    const files = content?.files || {}
    // Try to find page by full path first (for nested pages)
    let page = findPageByPath(urlPath, pages)
    // Fallback to slug lookup for single-segment paths
    if (!page && !urlPath.includes('/')) {
        const result = findPageBySlug(urlPath, pages)
        page = result?.page || null
    }

    // Get parent page info for analytics context
    const parentPage = useMemo(() => {
        if (!page) return null
        const parentId = getParentId(page)
        return parentId ? pages[parentId] : null
    }, [page, pages])

    // Count images in gallery
    const imageCount = useMemo(() => {
        if (!page?.gallery?.images) return 0
        return page.gallery.images.length
    }, [page?.gallery?.images])

    // Track project viewed for analytics with full context
    useEffect(() => {
        if (page?.name) {
            trackProjectViewed({
                name: page.name,
                description: page.description || page.name,
                parentName: parentPage?.name,
                galleryType: page.gallery?.type || 'html',
                imageCount,
            })
        }
    }, [page?.name, page?.description, page?.gallery?.type, parentPage?.name, imageCount])

    if (page === null) {
        return (
            <section className="bg-background">
                <NotFound />
            </section>
        )
    }

    return <PublicPageView page={page} pages={pages} files={files} content={content} urlBase={urlBase} theme={theme} />
}

// Separate from the route so a page that appears while the route is mounted
// (a draft arriving in the editor frame) does not change the hook order.
function PublicPageView({ page, pages, files, content, urlBase, theme }) {
    const navigate = useNavigate()
    const inEditor = useEditorFrame()
    const textEditing = useBlockEditing('legacy-richtext')

    // Pages saved from the blocks canvas carry their own layout; everything else
    // still renders through the original path below. An empty blocks array means
    // the editor cleared the stack — do not fall back to the legacy gallery.
    const useBlocks = Array.isArray(page.blocks)

    // Extract page-specific gallery properties
    const galleryType = page.gallery?.type || 'none'
    const pageImages = page.gallery?.images || []
    const pageBreakpoints = page.gallery?.breakpoints || null
    const pageColumns = page.gallery?.columns || 5
    const pageSpreadGap = page.gallery?.spreadGap || 0
    const pageGap = page.gallery?.gap || 4
    const pageCrops = page.gallery?.crops || null
    const pageLinks = page.gallery?.links || []
    
    // Calculate the full path for the page (handles nested pages)
    // This is needed for correct image linking in Masonry
    const fullPagePath = getPagePath(page, pages)

    // For HTML content, use 30/70 vertical distribution instead of centered
    const isHtmlOnly = galleryType !== 'masonry' && galleryType !== 'slideshow'

    // Get og:image - inherit from parent pages and home if not set on current page
    const ogImageId = useMemo(() => {
        // Check current page first
        if (page.og?.image || page.ogImage) {
            return page.og?.image || page.ogImage
        }
        
        // Traverse up the parent chain to find an image
        let currentPage = page
        const visited = new Set()
        while (currentPage) {
            const parentId = getParentId(currentPage)
            if (!parentId || visited.has(parentId)) break
            visited.add(parentId)
            
            const parent = pages[parentId]
            if (parent?.og?.image || parent?.ogImage) {
                return parent?.og?.image || parent?.ogImage
            }
            currentPage = parent
        }
        
        // Fall back to home page og:image
        const home = content?.home
        if (home?.og?.image || home?.ogImage) {
            return home?.og?.image || home?.ogImage
        }
        
        return null
    }, [page, pages, content?.home])
    
    const baseUrl = urlBase?.replace(/\/$/, '') || ''
    const ogImageFile = ogImageId ? files[ogImageId] : null
    const siteTitle = shareSiteTitle(content, theme)
    
    // Get og:description - inherit from parent pages if not set on current page.
    // Templates ({parent}, {site}, {title}) resolve against the page that wrote them.
    const ogDescriptionSource = useMemo(() => {
        if (page.og?.description) return { text: page.og.description, owner: page }
        
        let currentPage = page
        const visited = new Set()
        while (currentPage) {
            const parentId = getParentId(currentPage)
            if (!parentId || visited.has(parentId)) break
            visited.add(parentId)
            
            const parent = pages[parentId]
            if (parent?.og?.description) return { text: parent.og.description, owner: parent }
            currentPage = parent
        }
        
        return null
    }, [page, pages])

    const shareTitle = useMemo(
        () => resolvePageShareTitle(page, { pages, siteTitle }),
        [page, pages, siteTitle],
    )

    const ogDescription = ogDescriptionSource
        ? resolveShareTitle(
            ogDescriptionSource.text,
            shareTitleVarsForPage(ogDescriptionSource.owner, { pages, siteTitle }),
            '',
            { strict: true },
        )
        : ''
    
    const metaTags = compactMeta([
        { name: 'robots', content: page.isPublic ? 'follow' : 'none' },
        { property: 'og:title', content: shareTitle },
        { name: 'twitter:title', content: shareTitle },
        { name: 'description', content: ogDescription },
        { property: 'og:description', content: ogDescription },
        { name: 'twitter:description', content: ogDescription },
        ...shareImageMeta(ogImageFile, baseUrl),
    ])
    
    // Check for fullscreen background display type
    const isBackgroundMode = galleryType === 'background' && hasBackground(page.backgroundImage, files)
    
    // Get follow-up page URL for background mode
    const followUpUrl = useMemo(() => {
        if (!isBackgroundMode || !page.advanceUrl) return null
        // Handle "home" as special value
        if (page.advanceUrl === 'home') return '/'
        const followUpPage = pages[page.advanceUrl]
        if (!followUpPage) return null
        const path = getPagePath(followUpPage, pages)
        return path ? `/${path}` : null
    }, [isBackgroundMode, page.advanceUrl, pages])
    
    // Handle navigation to follow-up page
    const handleAdvance = useCallback(() => {
        if (followUpUrl) {
            navigate(followUpUrl)
        }
    }, [followUpUrl, navigate])

    // In Appearance, only block advance while overlay text is open for editing.
    const canAdvance = Boolean(followUpUrl) && !textEditing
    
    // Keyboard navigation for background mode
    useEffect(() => {
        if (!isBackgroundMode || !canAdvance) return
        
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'Enter') {
                e.preventDefault()
                handleAdvance()
            }
        }
        
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [isBackgroundMode, canAdvance, handleAdvance])
    
    // For background mode, render a full-viewport clickable area with centered content
    if (isBackgroundMode) {
        return (
            <section
                className={cx(
                    // No bg-background here - let the image be the background
                    'fs-stage flex flex-col items-center justify-center',
                    { 'cursor-pointer': canAdvance }
                )}
                aria-label={page.name}
                data-page-path="page.advance"
                onClick={canAdvance ? (event) => {
                    if (inEditor && event.target?.closest?.('[data-page-path="page.block"], [data-page-path="page.content"]')) return;
                    handleAdvance();
                } : undefined}
            >
                <Helmet
                    title={shareTitle}
                    meta={metaTags}
                />
                <FullscreenBackground
                    backgroundImage={page.backgroundImage}
                    files={files}
                />
                {/* Overlay text + vectors; overflow clipped (no scroll) so vectors may exceed the viewport */}
                <div
                    className={cx(
                        'relative z-10 text-center w-full overflow-visible',
                        inEditor && 'cursor-auto',
                    )}
                    style={{ zIndex: 10 }}
                    data-page-path="page.block"
                    data-page-id="legacy-richtext"
                    data-editor-overlay={inEditor ? '1' : undefined}
                    data-block-editing={textEditing ? '1' : undefined}
                    onClick={(inEditor || textEditing) ? (event) => event.stopPropagation() : undefined}
                >
                    <OverlayBlocks
                        page={page}
                        pages={pages}
                        files={files}
                        theme={theme}
                        gallery="background"
                        editing={inEditor ? textEditing : undefined}
                        editPath="content"
                    />
                </div>
            </section>
        )
    }
    
    return (
        <section
            className={cx(
                // shrink-0: the section is as tall as its content, so a tall gallery
                // is not clipped to the viewport. min-h-full still fills a short page.
                // flex-1 (basis 0% and shrink) together with min-h-full laid the text
                // out twice during navigation, and the copies overlapped until the
                // next frame.
                'w-full shrink-0 flex flex-col items-center relative',
                { 
                    // A slideshow's viewer is fixed or portalled to body. Over a
                    // directory the section sits in the media layer above that
                    // portal, so any height here swallows every tap and swipe.
                    'min-h-full': galleryType !== 'slideshow',
                    'justify-start pb-[24vh]': isHtmlOnly && !useBlocks,
                    // A single text page is a block in the editor, with no extra
                    // top inset. The same page is saved without a blocks list, so
                    // the live page must not add the old 8vh/12vh pad either.
                    'justify-center': !isHtmlOnly,
                }
            )}
            aria-label={page.name}
            data-page-path="page.display"
        >
            <Helmet
                title={shareTitle}
                meta={metaTags}
            />
            {/* A page saved from the blocks canvas renders its blocks. Pages that
                have never been edited there have no blocks array and fall through
                to the original render path below, unchanged. Slideshow is excluded
                because it owns the viewport and its own text overlay. An empty
                blocks array is intentional and must not revive the legacy gallery. */}
            {useBlocks && galleryType !== 'slideshow' && (
                <BlockRenderer
                    blocks={page.blocks}
                    page={page}
                    pages={pages}
                    files={files}
                    theme={theme}
                    fullPagePath={fullPagePath}
                />
            )}
            {!useBlocks && galleryType !== 'slideshow' && (
                <Html 
                    content={page.content} 
                    contentMaxWidth={page.contentMaxWidth} 
                    name={page.name}
                    pages={pages} 
                    files={files} 
                    gallery={galleryType} 
                />
            )}
            {!useBlocks && galleryType === 'masonry' && (
                <Masonry 
                    images={pageImages}
                    url={fullPagePath}
                    columns={pageColumns}
                    breakpoints={pageBreakpoints}
                    gap={pageGap}
                    crops={pageCrops}
                />
            )}
            {!useBlocks && galleryType === 'directory' && (
                <DirectoryMasonry
                    links={pageLinks}
                    columns={pageColumns}
                    breakpoints={pageBreakpoints}
                    gap={pageGap}
                />
            )}
            {galleryType === 'slideshow' && (
                <Slideshow 
                    images={pageImages}
                    url={fullPagePath}
                    content={page.content}
                    page={page}
                    spreadGap={pageSpreadGap}
                />
            )}
        </section>
    )
}

PublicPageRoute.propTypes = {
    match: PropTypes.object,
}

PublicPageView.propTypes = {
    page: PropTypes.object.isRequired,
    pages: PropTypes.object.isRequired,
    files: PropTypes.object.isRequired,
    content: PropTypes.object,
    urlBase: PropTypes.string,
    theme: PropTypes.object,
}

export default PublicPageRoute
